/**
 * metaPixel.js — Centralised Meta Pixel (fbq) event helper.
 *
 * Why a helper file:
 *  • Defensive guards (fbq might not have loaded yet, ad-blocker present, etc.)
 *  • Deduplication: some events (Lead, CompleteRegistration) MUST fire only
 *    once per user lifetime to keep Meta's ad-optimization signal clean.
 *  • Centralised event-name catalogue → no typos, easy to audit.
 *  • Easy to swap out / add a second pixel later without hunting through pages.
 *
 * Meta event taxonomy (standard events we emit):
 *  ─────────────────────────────────────────────────────────────────────
 *  ViewContent       → user opened a course detail page (course interest)
 *  Lead              → user crossed a real-intent threshold (first lab)
 *  AddToCart         → user expressed buy intent (pricing page open)
 *  InitiateCheckout  → user moved to actual payment step
 *  Purchase          → confirmed paid transaction (with value + currency)
 *  ─────────────────────────────────────────────────────────────────────
 *  These map directly to Meta Ads Manager's conversion-optimisation pipeline:
 *  the more Purchase + InitiateCheckout signals Meta receives, the cheaper
 *  per-paying-customer the ad spend becomes (typically 40-60% CPA drop after
 *  ~50 purchase events accumulated).
 */

const LS_KEY_FIRST_LAB = 'meta_lead_first_lab_fired_v1';

const safeFbq = (...args) => {
  if (typeof window === 'undefined') return;
  if (typeof window.fbq !== 'function') {
    // ad-blocker or pixel not loaded yet — silently noop
    return;
  }
  try {
    window.fbq(...args);
    // light dev visibility — easy to verify in browser console
    // eslint-disable-next-line no-console
    console.log('📊 Meta Pixel:', args[0], args[1], args[2] || '');
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('⚠️ Meta Pixel error (non-critical):', err);
  }
};

// ──────────────────────────────────────────────────────────────────────────
// ViewContent — course detail page opened
// content_ids + content_name help Meta build interest-based audiences
// ──────────────────────────────────────────────────────────────────────────
export const trackCourseView = (courseId, courseName) => {
  if (!courseId) return;
  safeFbq('track', 'ViewContent', {
    content_type: 'course',
    content_ids: [courseId],
    content_name: courseName || courseId,
    content_category: 'training_course',
  });
};

// ──────────────────────────────────────────────────────────────────────────
// Lead — fires ONCE per user when they start their first lab
// Stored in localStorage so refreshes / second labs don't dilute the signal.
// (Meta's deduplication tolerance is per-browser, hence localStorage is fine.)
// ──────────────────────────────────────────────────────────────────────────
export const trackFirstLabLead = (courseId, labId) => {
  try {
    if (localStorage.getItem(LS_KEY_FIRST_LAB) === '1') return;
    safeFbq('track', 'Lead', {
      content_name: 'first_lab_started',
      content_category: courseId || 'unknown',
      lab_id: labId || 'unknown',
    });
    localStorage.setItem(LS_KEY_FIRST_LAB, '1');
  } catch (err) {
    // localStorage disabled (rare) — fire anyway so we don't lose the signal
    safeFbq('track', 'Lead', { content_name: 'first_lab_started' });
  }
};

// ──────────────────────────────────────────────────────────────────────────
// AddToCart — pricing page visited (treated as "added to cart" in Meta's funnel)
// ──────────────────────────────────────────────────────────────────────────
export const trackPricingView = () => {
  safeFbq('track', 'AddToCart', {
    content_type: 'pricing_page',
    content_name: 'pricing_viewed',
  });
};

// ──────────────────────────────────────────────────────────────────────────
// InitiateCheckout — Razorpay checkout flow opened
// We pass course_id + amount when known so Meta sees real cart values
// ──────────────────────────────────────────────────────────────────────────
export const trackCheckoutStart = ({ courseId, amount, currency = 'INR' } = {}) => {
  const payload = {
    content_type: 'course',
    content_ids: courseId ? [courseId] : [],
    content_name: courseId || 'unknown',
  };
  if (typeof amount === 'number' && amount > 0) {
    payload.value = amount;
    payload.currency = currency;
  }
  safeFbq('track', 'InitiateCheckout', payload);
};

// ──────────────────────────────────────────────────────────────────────────
// Purchase — confirmed Razorpay success
// CRITICAL: value + currency are mandatory for Meta to calculate ROAS.
// We MUST fire this only after Razorpay verifies the payment (not on click).
// ──────────────────────────────────────────────────────────────────────────
export const trackPurchase = ({
  amount,
  currency = 'INR',
  courseId,
  paymentId,
} = {}) => {
  if (typeof amount !== 'number' || amount <= 0) {
    // eslint-disable-next-line no-console
    console.warn('⚠️ trackPurchase called without a valid amount — skipping');
    return;
  }
  safeFbq('track', 'Purchase', {
    value: amount,
    currency,
    content_type: 'course',
    content_ids: courseId ? [courseId] : [],
    content_name: courseId || 'unknown',
    order_id: paymentId || undefined,
  });
};

export default {
  trackCourseView,
  trackFirstLabLead,
  trackPricingView,
  trackCheckoutStart,
  trackPurchase,
};
